import { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import './FileUploadComponent.css';

/**
 * FileUploadComponent
 * Manages the drag-and-drop interface and file selection
 */
const FileUploadComponent = ({ maxFileSize, allowedTypes, onUploadComplete }) => {
  const [isDragActive, setIsDragActive] = useState(false);
  const fileInputRef = useRef(null);

  /**
   * Handle drag enter event - activate drop zone
   */
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  /**
   * Handle drag leave event - deactivate drop zone
   */
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if we're leaving the container entirely (not just entering a child)
    const relatedTarget = e.relatedTarget;
    if (!relatedTarget || !e.currentTarget.contains(relatedTarget)) {
      setIsDragActive(false);
    }
  };

  /**
   * Handle drag over event - required for drop to work
   */
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  /**
   * Handle file drop event
   */
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Immediately reset drag state
    setIsDragActive(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  /**
   * Handle click on upload area
   */
  const handleClick = () => {
    fileInputRef.current?.click();
  };

  /**
   * Handle file input change
   */
  const handleFileInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  /**
   * Process the selected file
   */
  const handleFile = (file) => {
    onUploadComplete(file);
  };

  return (
    <div
      className={`file-upload-container ${isDragActive ? 'drag-active' : ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
      aria-label="Upload file area"
    >
      <input
        ref={fileInputRef}
        type="file"
        accept={allowedTypes.join(',')}
        onChange={handleFileInputChange}
        style={{ display: 'none' }}
        aria-hidden="true"
      />
      
      <div className="upload-icon">
        <svg
          width="64"
          height="64"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
      </div>

      <div className="upload-text">
        <p className="upload-title">
          {isDragActive ? '📂 Drop file here!' : 'Drag and drop your file here'}
        </p>
        {!isDragActive && (
          <>
            <p className="upload-subtitle">
              or click to browse
            </p>
            <p className="upload-info">
              Supported formats: {allowedTypes.join(', ')} • Max size: {(maxFileSize / (1024 * 1024)).toFixed(0)}MB
            </p>
          </>
        )}
        {isDragActive && (
          <p className="upload-subtitle">
            Release to upload your file
          </p>
        )}
      </div>
    </div>
  );
};

FileUploadComponent.propTypes = {
  maxFileSize: PropTypes.number.isRequired,
  allowedTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  onUploadComplete: PropTypes.func.isRequired
};

export default FileUploadComponent;
